/* eslint-disable no-unused-vars */
import { useState } from 'react';
import { fd } from '../theme';
import Ic from '../Icon';
import { printDocument, generateChecklistHTML } from './PDFExport';

const MOVE_IN_ITEMS = [
  { section:'Living Room', items:['Walls & Ceiling','Floors','Windows & Blinds','Light Fixtures','Outlets & Switches','Doors & Locks'] },
  { section:'Kitchen',     items:['Countertops','Cabinets','Appliances','Sink & Faucet','Garbage Disposal','Floors'] },
  { section:'Bathroom',    items:['Toilet','Sink & Faucet','Shower/Tub','Mirrors','Exhaust Fan','Floors & Grout'] },
  { section:'Bedroom(s)',  items:['Walls & Ceiling','Closet Doors','Windows & Blinds','Floors','Outlets'] },
  { section:'Exterior',    items:['Entry Door & Lock','Patio/Balcony','Parking Area'] },
  { section:'Systems',     items:['HVAC / Thermostat','Water Heater','Smoke Detectors','Carbon Monoxide Detector','Keys Provided'] },
];

const CONDITION_OPTIONS = ['Good','Fair','Poor','N/A'];
const CONDITION_COLORS  = { Good:'bg', Fair:'by', Poor:'br', 'N/A':'b' };

function buildBlank() {
  const s = {};
  MOVE_IN_ITEMS.forEach(sec => sec.items.forEach(item => {
    s[`${sec.section}::${item}`] = { condition:'Good', note:'' };
  }));
  return s;
}

export default function Checklists({ isLL, activeTenant, tenants = [], checklists = [], addChecklist, updateChecklist }) {
  const [selTenant,  setSelTenant]  = useState(activeTenant?.id || (tenants[0]?.id ?? null));
  const [checkType,  setCheckType]  = useState('movein');
  const [localItems, setLocalItems] = useState({});
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);

  const effectiveTid = isLL ? selTenant : activeTenant?.id;

  // Find existing checklist for this tenant + type
  const existing = checklists.find(c =>
    (c.tenant_id === effectiveTid || c.tenant_id === String(effectiveTid)) &&
    c.check_type === checkType
  );

  // Merge: DB data → local edits
  const baseItems = existing?.items || buildBlank();
  const checks    = (localItems[`${effectiveTid}::${checkType}`]) || baseItems;

  const setItem = (key, field, val) => {
    const stateKey = `${effectiveTid}::${checkType}`;
    setLocalItems(prev => ({
      ...prev,
      [stateKey]: { ...(prev[stateKey] || checks), [key]: { ...(checks[key] || {}), [field]: val } },
    }));
    setSaved(false);
  };

  const saveChecklist = async () => {
    if (!effectiveTid) return;
    setSaving(true);
    const payload = {
      tenant_id:   effectiveTid,
      tenant_name: tenantName(effectiveTid),
      check_type:  checkType,
      items:       checks,
    };
    if (existing && updateChecklist) {
      await updateChecklist(existing.id, { items: checks });
    } else if (addChecklist) {
      await addChecklist(payload);
    }
    setSaved(true);
    setSaving(false);
  };

  const tenantName = tid => {
    const t = tenants.find(t => t.id === tid || t.id === String(tid));
    return t ? (t.full_name || t.name) : '—';
  };

  const total = Object.keys(checks).length;
  const good  = Object.values(checks).filter(v => v.condition === 'Good').length;
  const poor  = Object.values(checks).filter(v => v.condition === 'Poor').length;

  return (
    <div>
      <div className="card">
        <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
          {isLL && (
            <div className="fi" style={{ minWidth:180 }}>
              <label>Tenant</label>
              <select value={selTenant||''} onChange={e => { setSelTenant(e.target.value); setSaved(false); }}>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name||t.name}</option>)}
              </select>
            </div>
          )}
          <div className="fi" style={{ minWidth:160 }}>
            <label>Checklist Type</label>
            <select value={checkType} onChange={e => { setCheckType(e.target.value); setSaved(false); }}>
              <option value="movein">Move-In Inspection</option>
              <option value="moveout">Move-Out Inspection</option>
              <option value="routine">Routine Inspection</option>
            </select>
          </div>
          {existing && (
            <div style={{ fontSize:11, color:'#484f58' }}>Last saved: {fd(existing.completed_at)}</div>
          )}
          <div style={{ marginLeft:'auto', display:'flex', gap:6, alignItems:'flex-end', paddingBottom:1 }}>
            {[{v:good,l:'Good',c:'rgba(34,197,94,.1)',tc:'#4ade80'},{v:poor,l:'Issues',c:'rgba(248,81,73,.1)',tc:'#f85149'},{v:total,l:'Total',c:'#21262d',tc:'#fff'}].map(x => (
              <div key={x.l} style={{ textAlign:'center', padding:'4px 12px', background:x.c, borderRadius:8 }}>
                <div style={{ fontSize:18, fontWeight:900, color:x.tc, fontFamily:"'Playfair Display',serif" }}>{x.v}</div>
                <div style={{ fontSize:10, color:'#484f58', textTransform:'uppercase', letterSpacing:1 }}>{x.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {!effectiveTid ? (
        <div className="card" style={{ textAlign:'center', padding:'40px' }}>
          <Ic n="move" s={36} c="#484f58"/>
          <div style={{ marginTop:12, color:'#484f58', fontSize:13 }}>Select a tenant to view their checklist</div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom:8, display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:13, color:'#8b949e' }}>
              {isLL ? `${tenantName(effectiveTid)} — ` : ''}
              {checkType === 'movein' ? 'Move-In Inspection' : checkType === 'moveout' ? 'Move-Out Inspection' : 'Routine Inspection'}
            </span>
          </div>

          {MOVE_IN_ITEMS.map(section => (
            <div key={section.section} className="card" style={{ marginBottom:12 }}>
              <div className="card-hd" style={{ marginBottom:10 }}>
                <h3 style={{ fontSize:13 }}>{section.section}</h3>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
                {section.items.map(item => {
                  const key = `${section.section}::${item}`;
                  const val = checks[key] || { condition:'Good', note:'' };
                  return (
                    <div key={item} style={{ display:'flex', alignItems:'center', gap:12, padding:'9px 0', borderBottom:'1px solid #21262d' }}>
                      <div style={{ flex:1, fontSize:13, color:'#f5f0e8' }}>{item}</div>
                      <div style={{ display:'flex', gap:4 }}>
                        {CONDITION_OPTIONS.map(c => (
                          <button key={c} onClick={() => setItem(key,'condition',c)}
                            className={`b ${val.condition===c ? CONDITION_COLORS[c] : ''}`}
                            style={{ border: val.condition===c ? undefined : '1px solid #2d333b', background: val.condition===c ? undefined : 'transparent', color: val.condition===c ? undefined : '#484f58', cursor:'pointer', fontSize:10, padding:'2px 8px', borderRadius:20, fontFamily:'Outfit,sans-serif', fontWeight:700 }}>
                            {c}
                          </button>
                        ))}
                      </div>
                      <input
                        style={{ background:'#21262d', border:'1px solid #2d333b', borderRadius:6, padding:'5px 8px', color:'#8b949e', fontSize:11, fontFamily:'Outfit,sans-serif', width:140, outline:'none' }}
                        value={val.note}
                        onChange={e => setItem(key,'note',e.target.value)}
                        placeholder="Note (optional)"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:4 }}>
            <button className="btn btn-gh" onClick={() => printDocument(generateChecklistHTML(checks, tenantName(effectiveTid), 'movein', MOVE_IN_ITEMS))}>
              <Ic n="dl" s={13}/> Print Move-In
            </button>
            <button className="btn btn-gh" onClick={() => printDocument(generateChecklistHTML(checks, tenantName(effectiveTid), 'moveout', MOVE_IN_ITEMS))}>
              <Ic n="dl" s={13}/> Print Move-Out
            </button>
            <button className="btn btn-g" onClick={saveChecklist} disabled={saving || (!addChecklist && !updateChecklist)}>
              <Ic n="check" s={13}/>
              {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Inspection'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
